import {
  add,
  addWeeks,
  differenceInMilliseconds,
  differenceInSeconds,
  format,
  formatISO,
  getISODay,
  getISOWeek,
  parseISO,
  setISODay,
} from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

/**
 * Gets the browser's timezone using native Intl API
 * @returns The timezone string (e.g. "America/New_York")
 */
export function getBrowserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Finds the next even-numbered week's Tuesday
 * @param {Date} [date] - The date to find the next even-numbered week's Tuesday for
 * @returns {string} The formatted date string (e.g. "April 22, 2025")
 */
export function findNextEvenWeekTuesday(date?: Date): string {
  // Use current date if none provided
  const targetDate = date ? new Date(date) : new Date();

  // Get the current week number
  const weekNumber = getISOWeek(targetDate);

  // Get the current day of week (1-7, where 1 is Monday, 2 is Tuesday)
  const dayOfWeek = getISODay(targetDate);

  // Create a new date to avoid mutating the input
  let resultDate = new Date(targetDate);

  if (weekNumber % 2 === 0) {
    // Handle even-numbered weeks
    if (dayOfWeek === 2)
      // If today is Tuesday, return today
      return format(resultDate, "MMMM do, yyyy");

    if (dayOfWeek < 2)
      // If today is Sunday or Monday, return the upcoming Tuesday
      resultDate = setISODay(resultDate, 2);
    // If it's Wednesday through Saturday, return the Tuesday after next
    else resultDate = setISODay(addWeeks(resultDate, 2), 2);
  } else {
    // Handle odd-numbered weeks
    if (dayOfWeek <= 2)
      // If today is Sunday, Monday or Tuesday, return next week's Tuesday
      resultDate = setISODay(addWeeks(resultDate, 1), 2);
    // If it's Wednesday through Saturday, return next week's Tuesday
    else resultDate = setISODay(addWeeks(resultDate, 1), 2);
  }

  return format(resultDate, "MMMM do, yyyy");
}

/**
 * Returns the earlier of the provided date or current date
 * @param date - The date to compare (e.g. new Date("2025-04-22"))
 * @returns The earlier date (e.g. new Date("2025-04-22"))
 */
export function toMinDate({ date }: { date?: Date | null }): Date {
  if (!date) return new Date();

  const currentDate = new Date();

  return new Date(Math.min(date.getTime(), currentDate.getTime()));
}

/**
 * Formats a date using the provided format string with browser timezone
 * @param date - The date to format
 * @param formatString - The format string to use
 * @param timezone - Optional timezone, defaults to browser timezone
 * @returns The formatted date string
 */
export function formatDate(
  date: Date | string | null | undefined,
  formatString: string,
  timezone?: string | null,
): string | null {
  if (!date) return null;

  const tz = timezone || getBrowserTimezone();

  // If it's a string, parse it directly with formatInTimeZone
  if (typeof date === "string") {
    // Parse the ISO string while preserving timezone information
    return formatInTimeZone(parseISO(date), tz, formatString);
  }

  // For Date objects, use the existing flow
  return formatInTimeZone(date, tz, formatString);
}

/**
 * Checks if two dates have different times
 * @param date1 - The first date
 * @param date2 - The second date
 * @returns True if the dates are different by more than 60 seconds
 */
export function areDifferentTimes(
  date1?: Date | string | null,
  date2?: Date | string | null,
): boolean {
  if (!date1 && !date2) return false;
  if (!date1 || !date2) return true;

  if (typeof date1 === "string") date1 = new Date(date1);
  if (typeof date2 === "string") date2 = new Date(date2);

  const diffInSeconds = Math.abs(differenceInSeconds(date1, date2));
  return diffInSeconds > 60;
}

/**
 * Sets the time of a date
 * @param date - The date to set the time on
 * @param hours - The hours to set the time to
 * @param minutes - The minutes to set the time to
 * @param seconds - The seconds to set the time to
 * @param ms - The milliseconds to set the time to
 */
export function setTime(
  date: Date,
  {
    hours = 0,
    minutes = 0,
    seconds = 0,
    ms = 0,
  }: { hours?: number; minutes?: number; seconds?: number; ms?: number },
): Date {
  const newDate = new Date(date);
  newDate.setHours(hours);
  newDate.setMinutes(minutes);
  newDate.setSeconds(seconds);
  newDate.setMilliseconds(ms);
  return newDate;
}

/**
 * Extracts timezone offset from a date string
 * @param dateTimeString - ISO date string or null
 * @returns Formatted timezone offset (e.g., "+01:00") or null if input is invalid
 */
export function getTimezoneFromString(
  dateTimeString: string | null,
): string | null {
  if (!dateTimeString) return null;

  try {
    const date = parseISO(dateTimeString);
    const formatted = formatISO(date);
    // Extract timezone portion from ISO string (last 6 characters including the sign)
    return formatted.slice(-6);
  } catch {
    return null;
  }
}

/**
 * Converts a value between different time units
 * @param value - The value to convert
 * @param oldUnit - The unit of the value
 * @param newUnit - The unit to convert to
 * @returns The converted value
 */
export function convertUnit(
  value: number,
  oldUnit: string,
  newUnit: string,
): number {
  // Create two dates and use the difference to calculate the conversion
  const baseDate = new Date();
  const modifiedDate = add(baseDate, { [oldUnit]: value });

  // Get difference in milliseconds
  const diffMs = differenceInMilliseconds(modifiedDate, baseDate);

  // Convert milliseconds to target unit
  const unitInMs: Record<string, number> = {
    seconds: 1000,
    minutes: 60 * 1000,
    hours: 60 * 60 * 1000,
    days: 24 * 60 * 60 * 1000,
    weeks: 7 * 24 * 60 * 60 * 1000,
    months: 30.44 * 24 * 60 * 60 * 1000, // Average month length
    years: 365.25 * 24 * 60 * 60 * 1000, // Average year length with leap years
  };

  if (!unitInMs[oldUnit] || !unitInMs[newUnit])
    throw new Error(`Invalid unit: ${!unitInMs[oldUnit] ? oldUnit : newUnit}`);

  const result = diffMs / unitInMs[newUnit];

  // Round to 2 decimal places
  return Math.round(result * 100) / 100;
}

/**
 * Formats a duration in seconds to a human-readable MM:SS format
 * @param seconds - The duration in seconds
 * @returns The formatted duration string (e.g. "1:30")
 */
export function formatMinutes(seconds?: number | null): string {
  if (!seconds) return "0:00";

  // Calculate hours, minutes, and seconds
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    const totalMinutes = hours * 60 + minutes;
    return `${totalMinutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }

  return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}
